import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import swal from "sweetalert";
import HttpCommon from "../../Utils/http-common";
import {
  CMS_ROUTES,
  MUST_SET_PASSWORD_KEY,
  TOKEN_KEY,
} from "../../Frontend/Constant";

/** Public / transitional routes — finish login first; do not block these. */
const HIDE_ON_PATHS = new Set([
  ...CMS_ROUTES,
  "/whitelist-login",
  "/dashboard",
  "/dashboard/",
]);

function shouldShowSetPasswordModal(pathname) {
  const flag = localStorage.getItem(MUST_SET_PASSWORD_KEY) === "true";
  const token = localStorage.getItem(TOKEN_KEY);
  const path = pathname?.split("?")[0] || "/";

  // Don't leave a stuck flag on the marketing/login site with no session.
  if (flag && !token && HIDE_ON_PATHS.has(path)) {
    localStorage.removeItem(MUST_SET_PASSWORD_KEY);
    return false;
  }

  if (!flag || !token) return false;
  if (HIDE_ON_PATHS.has(path)) return false;
  if (path.startsWith("/dashboard")) return false;
  return true;
}

export default function ForceSetPasswordModal() {
  const theme = useTheme();
  const location = useLocation();
  const primary = theme.palette.primary.main;
  const [open, setOpen] = useState(() =>
    shouldShowSetPasswordModal(window.location.pathname),
  );
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const sync = () => {
      setOpen(shouldShowSetPasswordModal(location.pathname));
    };
    sync();
    window.addEventListener("storage", sync);
    const id = setInterval(sync, 1000);
    return () => {
      window.removeEventListener("storage", sync);
      clearInterval(id);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const blockEscape = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", blockEscape, true);
    return () => window.removeEventListener("keydown", blockEscape, true);
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!newPassword || !confirmPassword) {
      setError("Both password fields are required.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await HttpCommon.post(
        "/user/whitelist-set-password",
        { newPassword, confirmPassword },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (res?.data?.success === 1) {
        localStorage.removeItem(MUST_SET_PASSWORD_KEY);
        setOpen(false);
        swal("Password saved", "You can continue using your account.", "success");
      } else {
        setError(res?.data?.message || "Unable to save password.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to save password.");
    } finally {
      setSubmitting(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      background: "#FFF",
      borderRadius: "6px",
      "& fieldset": {
        borderColor: primary,
      },
      "&:hover fieldset": {
        borderColor: primary,
      },
      "&.Mui-focused fieldset": {
        borderColor: primary,
        borderWidth: "1px",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 14px",
      fontSize: "14px",
      color: "#000",
    },
    "& .MuiInputLabel-root": {
      color: "#565868",
      fontSize: "14px",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: primary,
    },
  };

  return (
    <Modal
      open={open}
      // Required step — ignore backdrop / escape close from MUI
      onClose={() => {}}
      disableEscapeKeyDown
      aria-labelledby="force-set-password-title"
      aria-describedby="force-set-password-description"
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "92%", sm: 420 },
          bgcolor: "white",
          boxShadow: 24,
          p: 4,
          borderRadius: "8px",
          outline: "none",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography
          id="force-set-password-title"
          component="div"
          sx={{ color: "#565868", fontSize: "18px", fontWeight: 500 }}
        >
          Set up your password
        </Typography>
        <Typography
          id="force-set-password-description"
          sx={{
            color: "#6b6f80",
            fontSize: "14px",
            lineHeight: 1.5,
            mt: -0.5,
          }}
        >
          You&apos;re signed in. Create a password for this account so you can log in
          next time. This step is required and cannot be skipped.
        </Typography>

        <TextField
          label="New password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          autoComplete="new-password"
          disabled={submitting}
          fullWidth
          size="small"
          sx={fieldSx}
        />
        <TextField
          label="Confirm password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
          disabled={submitting}
          fullWidth
          size="small"
          sx={fieldSx}
        />

        {error ? (
          <Typography sx={{ color: "#da1f1f", fontSize: "13px", mt: -0.5 }}>
            {error}
          </Typography>
        ) : null}

        <Button
          type="submit"
          variant="contained"
          disabled={submitting}
          fullWidth
          sx={{
            mt: 1,
            py: 1.1,
            textTransform: "none",
            fontSize: "15px",
            fontWeight: 600,
            borderRadius: "8px",
            backgroundColor: primary,
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
            "&:hover": {
              backgroundColor: primary,
              filter: "brightness(0.95)",
            },
            "&.Mui-disabled": {
              backgroundColor: primary,
              color: "#fff",
              opacity: 0.7,
            },
          }}
        >
          {submitting ? "Saving…" : "Save password"}
        </Button>
      </Box>
    </Modal>
  );
}
